import React, { useState, useEffect } from 'react';
import {
  faEye,
  faEyeSlash,
  faEnvelope,
  faLock,
  faUser,
  faBuilding,
  faUsers,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import Alert from '../components/Alert';
import Input from '../components/Input';
import { AuthService } from '../services/authService';
import { OrganizationService } from '../services/organizationService';
import { COMPANY_SIZES } from '../types';
import { isSignupEnabled } from '../config/config';

// Define form data type locally since we're not using schema
interface SignupFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  organizationName: string;
  organizationSlogan: string;
  companySize: string;
}

const SignupPage: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Clear any existing organization data from storage when accessing signup
  useEffect(() => {
    OrganizationService.clearCurrentOrganizationFromStorage();
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors }
  } = useForm<SignupFormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      organizationName: '',
      organizationSlogan: '',
      companySize: ''
    }
  });

  const watchedValues = watch();

  const onSubmit = async (data: SignupFormData): Promise<void> => {
    if (!isSignupEnabled()) {
      setError('Cadastro não disponível no momento');
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      // Create user with Supabase Auth (includes captcha protection)
      // Store organization data in user metadata for later creation
      const { data: authData, error: authError } = await AuthService.signUp({
        name: data.name,
        email: data.email,
        password: data.password,
        organizationData: {
          organizationName: data.organizationName,
          organizationSlogan: data.organizationSlogan,
          companySize: data.companySize
        }
      });

      if (authError) {
        throw new Error(authError.message);
      }

      if (!authData.user) {
        throw new Error('Falha ao criar usuário');
      }

      // Redirect to login with success message
      // Organization will be created after email verification by the auth guard
      navigate('/login?signup=success');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Erro inesperado durante o cadastro';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  if (user && !loading) {
    return <Navigate to="/" replace />;
  }

  if (!isSignupEnabled()) {
    return <Navigate to="/login" replace />;
  }

  const companySizeOptions = COMPANY_SIZES.map(size => ({
    value: size,
    label: `${size} funcionários`
  }));

  return (
    <>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100">
              <img
                className="w-10 h-10 object-contain"
                src="/logoipsum-custom-logogram.svg"
                alt="OptoSoft Logo"
              />
            </div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">OptoSoft</h1>
        <p className="text-gray-600 text-sm">Sistema de Gestão Clínica</p>
      </div>

      {/* Signup Form */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="mb-5">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Criar Nova Conta</h2>
          <p className="text-gray-600 text-sm">Cadastre sua organização</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Personal Information Section */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Dados Pessoais</h3>

            {/* Name Field */}
            <Input
              {...register('name', {
                required: 'Nome é obrigatório',
                minLength: {
                  value: 2,
                  message: 'Nome deve ter pelo menos 2 caracteres'
                }
              })}
              id="name"
              label="Nome Completo"
              type="text"
              autoComplete="name"
              placeholder="Seu nome completo"
              leftIcon={faUser}
              error={errors.name?.message}
            />

            <div className="mt-4 grid grid-cols-1 gap-4">
              {/* Email Field */}
              <Input
                {...register('email', {
                  required: 'Email é obrigatório',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email deve ter um formato válido'
                  }
                })}
                id="email"
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="seu@email.com"
                leftIcon={faEnvelope}
                error={errors.email?.message}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Password Field */}
              <Input
                {...register('password', {
                  required: 'Senha é obrigatória',
                  minLength: {
                    value: 6,
                    message: 'Senha deve ter pelo menos 6 caracteres'
                  }
                })}
                id="password"
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                leftIcon={faLock}
                rightIcon={showPassword ? faEyeSlash : faEye}
                onRightIconClick={togglePasswordVisibility}
                error={errors.password?.message}
              />

              {/* Confirm Password Field */}
              <Input
                {...register('confirmPassword', {
                  required: 'Confirmação de senha é obrigatória',
                  validate: value => {
                    const password = watchedValues.password;
                    return value === password || 'Senhas não conferem';
                  }
                })}
                id="confirmPassword"
                label="Confirmar Senha"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                leftIcon={faLock}
                rightIcon={showPassword ? faEyeSlash : faEye}
                onRightIconClick={togglePasswordVisibility}
                error={errors.confirmPassword?.message}
              />
            </div>
          </div>

          {/* Organization Information Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Dados da Organização</h3>

            {/* Organization Name Field */}
            <Input
              {...register('organizationName', {
                required: 'Nome da organização é obrigatório',
                minLength: {
                  value: 2,
                  message: 'Nome da organização deve ter pelo menos 2 caracteres'
                }
              })}
              id="organizationName"
              label="Nome da Organização"
              type="text"
              placeholder="Nome da sua clínica ou consultório"
              leftIcon={faBuilding}
              error={errors.organizationName?.message}
            />

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Organization Slogan Field */}
              <Input
                {...register('organizationSlogan')}
                id="organizationSlogan"
                label="Slogan (Opcional)"
                type="text"
                placeholder="Slogan da sua organização"
              />

              {/* Company Size Field */}
              <div>
                <label
                  htmlFor="companySize"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Tamanho da Empresa
                </label>
                <div className="relative">
                  <select
                    {...register('companySize', {
                      required: 'Tamanho da empresa é obrigatório'
                    })}
                    id="companySize"
                    className={`w-full px-3 py-2 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.companySize ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecione o tamanho</option>
                    {companySizeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faUsers} className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                {errors.companySize && (
                  <p className="mt-1 text-sm text-red-600">{errors.companySize.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && <Alert variant="error" title="Erro no Cadastro" message={error} />}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={isLoading || !isValid}
            leftIcon={!isLoading ? faUserPlus : undefined}
          >
            {isLoading ? 'Criando Conta...' : 'Criar Conta'}
          </Button>
        </form>

        {/* Login Link */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            Já tem uma conta?{' '}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              Faça login aqui
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-6">
          <p className="text-xs text-gray-500 text-center">
            Desenvolvido por{' '}
            <a
              href="https://um0um.com.br"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              101 Soluções
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default SignupPage;
